const RETURN_TO_PARAM = 'return_to';

export interface NoteRouteLocation {
  pathname: string;
  search: string;
  hash: string;
}

export interface NewNoteRouteOptions {
  appointmentId?: string;
  patientId?: string;
  caseId?: string;
  templateId?: string;
  extra?: Record<string, string>;
}

/**
 * Extract the note return context from a location.
 *
 * If a `return_to` query param is already present, return it (preserving
 * nested context). Otherwise, use the current path/query/hash as the return
 * context, unless the current location is a note page, an auth page, or the
 * root path, in which case no context is returned.
 */
export function getReturnContextFromLocation(location: NoteRouteLocation): string | null {
  const params = new URLSearchParams(location.search);
  const existing = params.get(RETURN_TO_PARAM);
  if (existing) {
    // `URLSearchParams.get` already percent-decodes the value.
    return existing;
  }

  const { pathname, hash } = location;

  // Don't capture auth-only or note-compose pages as a return context.
  if (
    pathname === '/' ||
    pathname.startsWith('/login') ||
    pathname.startsWith('/demo-login') ||
    pathname.startsWith('/register') ||
    pathname.startsWith('/forgot-password') ||
    pathname.startsWith('/reset-password') ||
    pathname.startsWith('/notes/')
  ) {
    return null;
  }

  const cleaned = new URLSearchParams(location.search);
  cleaned.delete(RETURN_TO_PARAM);
  const query = cleaned.toString();
  return `${pathname}${query ? `?${query}` : ''}${hash}`;
}

/**
 * Get the current window location as a return context.
 * Returns null when called outside a browser.
 */
export function getReturnContext(): string | null {
  if (typeof window === 'undefined' || !window.location) {
    return null;
  }
  return getReturnContextFromLocation(window.location);
}

function buildReturnToParams(returnContext: string | null): URLSearchParams {
  const params = new URLSearchParams();
  if (returnContext) {
    params.set(RETURN_TO_PARAM, returnContext);
  }
  return params;
}

/**
 * Build a route to an existing note, preserving the originating return context.
 */
export function buildNoteRoute(
  noteId: string,
  returnContext: string | null = getReturnContext(),
): string {
  const encodedId = encodeURIComponent(noteId);
  const params = buildReturnToParams(returnContext);
  if (params.toString()) {
    return `/notes/${encodedId}?${params.toString()}`;
  }
  return `/notes/${encodedId}`;
}

/**
 * Build a route to the new-note editor, preserving the originating return context.
 */
export function buildNewNoteRoute(
  options: NewNoteRouteOptions = {},
  returnContext: string | null = getReturnContext(),
): string {
  const params = new URLSearchParams();

  if (options.appointmentId) {
    params.set('appointment_id', options.appointmentId);
  }
  if (options.patientId) {
    params.set('patient_id', options.patientId);
  }
  if (options.caseId) {
    params.set('case_id', options.caseId);
  }
  if (options.templateId) {
    params.set('template_id', options.templateId);
  }
  if (options.extra) {
    Object.entries(options.extra).forEach(([key, value]) => {
      params.set(key, value);
    });
  }

  const returnParams = buildReturnToParams(returnContext);
  returnParams.forEach((value, key) => {
    params.set(key, value);
  });

  const query = params.toString();
  return query ? `/notes/new?${query}` : '/notes/new';
}

/**
 * Decode a raw `return_to` search param value.
 */
export function decodeReturnTo(value: string | null): string | null {
  // `URLSearchParams.get` already percent-decodes the param value, so the
  // caller is passing a decoded string. Avoid double-decoding.
  return value || null;
}
